import { Button, Grid } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import { useAuthContext } from '../../context/auth';
import { useClaimContext } from '../../context/claim';
import { useErrorContext } from '../../context/ErrorProvider';
import { usePolicyContext } from '../../context/policy';
import { CLAIM_EDIT_BUTTON_TEXT } from '../../data/labelsAndMessages';
import { FastForm } from '../fastForm';
import { FORM_NAME } from '../fastForm/data/constants';
import { getDefaultPolicyValues } from '../fastForm/formBuilders';
import getClaimsForm from '../fastForm/formBuilders/getClaimForm';
import { successToast } from '../Toast';
import UpdateOpenClaimModal from './UpdateOpenClaimModal';

const ClaimDetails = () => {
	const authData = useAuthContext();
	const claimData = useClaimContext();
	const errorData = useErrorContext();
	const policyData = usePolicyContext();
	const { getValues, formState, reset } = useFormContext();
	const { hasPermission } = authData;
	const { dirtyFields } = formState;
	const {
		editMode,
		fields,
		saveFastForm,
		setLoading,
		setEditMode,
		setFields,
		claim,
	} = claimData;
	const { currentStatus } = claim;
	const { timezone } = authData.auth;
	const { isDirty } = formState;
	const { claimConfiguration } = policyData.productConfig;
	const isDisabled = !hasPermission('claim.update') || !editMode;
	const updateClaim = async () => {
		setLoading(true);
		try {
			const updatedClaim = await saveFastForm(getValues(), dirtyFields, reset);
			const fields = getClaimsForm({
				claim: updatedClaim.claim,
				timezone,
				claimConfiguration,
			});
			const defaultValues = getDefaultPolicyValues(fields, authData);
			reset(defaultValues);
			setFields(fields);

			if (currentStatus === 'open') {
				setEditMode(false);
			}

			successToast(`Claim updated successfully`);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to update claim');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const enableEditMode = () => {
		setEditMode(true);
	};

	const cancelEditMode = () => {
		// reset changes
		setEditMode(false);
		reset();
	};

	const claimDetailActions = () => {
		if (currentStatus === 'open' && !editMode) {
			return (
				<Button color='primary' onClick={enableEditMode}>
					{CLAIM_EDIT_BUTTON_TEXT()}
				</Button>
			);
		} else if (currentStatus === 'open' && editMode) {
			return (
				<>
					<UpdateOpenClaimModal />
					<Button color='primary' onClick={cancelEditMode} variant='text'>
						Cancel
					</Button>
				</>
			);
		} else if (currentStatus === 'pending') {
			return (
				<Button
					color='primary'
					onClick={updateClaim}
					variant='contained'
					disabled={!isDirty}
				>
					Save
				</Button>
			);
		} else {
			return null;
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid container item xs={12} justifyContent='flex-end'>
				<Grid item>
					{hasPermission('auxData.fetch') && (
						<Button component={RouterLink} to='../auxData'>
							Aux Data
						</Button>
					)}
				</Grid>
			</Grid>
			<FastForm
				formName={FORM_NAME.claim}
				form={fields}
				configVersion=''
				productName={policyData.productConfig.name}
				authData={authData}
				disabled={isDisabled}
			/>
			<Grid container item xs={12} justifyContent='flex-end'>
				{hasPermission('claim.update') && claimDetailActions()}
			</Grid>
		</Grid>
	);
};

export default ClaimDetails;
